/**
 * Setting the desk's targets for one window.
 *
 * One window at a time, and that is the reason this dialog opens from the
 * overview rather than from Settings. The operator is already looking at a
 * period tab when they decide the number is too low; the dialog is about the
 * window in front of them, and the other two are reached by moving the tab they
 * are already using. A single Settings panel would have shown nine fields to
 * somebody who came to change one.
 *
 * Every field may be left empty. Empty is not zero - it is "do not measure this
 * one", and the tile then draws exactly as it did before targets existed.
 */
import { useState } from "react";
import { Loader2, Target } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  formatGoalEuros,
  parseGoalCount,
  parseGoalEuros,
  putGoals,
  type CrmGoalSet,
  type GoalPeriod,
} from "../../../../lib/goals";
import { Btn, Modal } from "../../../ui";

export function GoalsModal({
  period,
  current,
  onClose,
  onSaved,
  onError,
}: {
  period: GoalPeriod;
  /** The targets already set for this window, so the fields open filled in. */
  current: CrmGoalSet;
  onClose: () => void;
  onSaved: (next: CrmGoalSet) => void;
  onError: () => void;
}) {
  const t = useT();
  const [revenue, setRevenue] = useState(() => formatGoalEuros(current.revenueCents));
  const [leads, setLeads] = useState(() => (current.leadsNew === null ? "" : String(current.leadsNew)));
  const [won, setWon] = useState(() => (current.dealsWon === null ? "" : String(current.dealsWon)));
  const [busy, setBusy] = useState(false);

  // Parsed on every keystroke so the hint under a field appears while the
  // number is still in front of the operator, not after the dialog has closed
  // over it. Null means "nothing usable here", which for an EMPTY field is the
  // correct answer and for a typed one is a mistake worth showing.
  const revenueCents = parseGoalEuros(revenue);
  const leadsNew = parseGoalCount(leads);
  const dealsWon = parseGoalCount(won);

  const badRevenue = revenue.trim() !== "" && revenueCents === null;
  const badLeads = leads.trim() !== "" && leadsNew === null;
  const badWon = won.trim() !== "" && dealsWon === null;
  const anyBad = badRevenue || badLeads || badWon;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (anyBad) return;
    setBusy(true);
    try {
      const res = await putGoals(period, { revenueCents, leadsNew, dealsWon });
      onSaved(res.goals[period]);
    } catch {
      onError();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t(`dash.goal.title.${period}`)}
      label={t(`dash.goal.title.${period}`)}
      onClose={onClose}
      className="max-w-[460px]"
    >
      <form onSubmit={submit} className="mt-1 flex flex-col gap-3">
        <div>
          <span className="crm-lab">{t("dash.goal.revenue")}</span>
          <label className="crm-inp">
            <input
              value={revenue}
              onChange={(e) => setRevenue(e.target.value)}
              placeholder="8000"
              inputMode="decimal"
              autoComplete="off"
              spellCheck={false}
              autoFocus
            />
          </label>
          {badRevenue ? <span className="mt-1 block text-[11.5px] text-destructive">{t("dash.goal.badNumber")}</span> : null}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className="crm-lab">{t("dash.goal.leads")}</span>
            <label className="crm-inp">
              <input
                value={leads}
                onChange={(e) => setLeads(e.target.value)}
                placeholder="20"
                inputMode="numeric"
                autoComplete="off"
              />
            </label>
            {badLeads ? <span className="mt-1 block text-[11.5px] text-destructive">{t("dash.goal.badNumber")}</span> : null}
          </div>
          <div>
            <span className="crm-lab">{t("dash.goal.won")}</span>
            <label className="crm-inp">
              <input
                value={won}
                onChange={(e) => setWon(e.target.value)}
                placeholder="5"
                inputMode="numeric"
                autoComplete="off"
              />
            </label>
            {badWon ? <span className="mt-1 block text-[11.5px] text-destructive">{t("dash.goal.badNumber")}</span> : null}
          </div>
        </div>

        {/* Both facts the operator needs before they type, and neither is
            guessable from the form: an empty field is a real answer, and there
            is no history behind these numbers. */}
        <p className="crm-sub">{t("dash.goal.emptyHint")}</p>
        <p className="crm-sub">{t("dash.goal.noHistoryHint")}</p>

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="brand" disabled={busy || anyBad}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Target className="size-4" />}
            {t("common.save")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
